import { useEffect, useMemo, useState } from 'react';
import { Bell, Mail, Smartphone, BellRing } from 'lucide-react';
import { toast } from 'sonner';
import { useAccess } from '../../contexts/AccessContext';
import {
  getNotificationRouting, getRoutingCatalog, saveNotificationRouting,
  type RoutingCatalog, type RoutingChannel, type RoutingMatrix,
} from '../../api/notificationRouting';

// Settings → Notifications: the routing matrix. One grid per event —
// rows are WHO (relations, resolved per employee at send time), columns are the
// channels, plus an optional "only from the Nth time" threshold.
//
// Nothing configured == the behaviour each event already had, so opening this
// page and saving without changes is a no-op.

const CHANNEL_META: Record<RoutingChannel, { label: string; icon: any; hint: string }> = {
  bell: { label: 'In-app', icon: Bell, hint: 'Notification bell' },
  push: { label: 'Push', icon: Smartphone, hint: 'Phone / desktop push' },
  email: { label: 'Email', icon: Mail, hint: 'Email' },
};

export default function NotificationRouting() {
  const { canManage } = useAccess();
  const readOnly = !canManage('set-notifications');

  const [catalog, setCatalog] = useState<RoutingCatalog | null>(null);
  const [matrix, setMatrix] = useState<RoutingMatrix>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getRoutingCatalog(), getNotificationRouting()])
      .then(([cat, rows]) => {
        if (cancelled) return;
        setCatalog(cat);
        setMatrix(rows);
      })
      .catch(() => { /* silent load failure — the page renders empty */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const events = useMemo(() => catalog?.events ?? [], [catalog]);
  const recipients = useMemo(() => catalog?.recipients ?? [], [catalog]);
  const channels = useMemo(() => catalog?.channels ?? [], [catalog]);

  const cellOf = (event: string, recipient: string) =>
    matrix[event]?.[recipient] ?? { bell: false, push: false, email: false, threshold: null };

  const setCell = (event: string, recipient: string, patch: Partial<RoutingMatrix[string][string]>) =>
    setMatrix((m) => ({
      ...m,
      [event]: { ...(m[event] ?? {}), [recipient]: { ...cellOf(event, recipient), ...patch } },
    }));

  const save = async () => {
    setSaving(true);
    try {
      setMatrix(await saveNotificationRouting(matrix));
      toast.success('Notification routing saved');
    } catch (e: any) {
      toast.error(e?.message || 'Could not save notification routing');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-full bg-[#faf8f3]">
      <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6">
        <div className="flex items-center gap-2">
          <BellRing className="h-6 w-6 text-emerald-700" />
          <h1 className="text-xl font-semibold">Notifications</h1>
          {readOnly && (
            <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold px-2.5 py-1">
              View only
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600 -mt-3">
          Who gets told about what. Each person's own preferences (Profile →
          Notifications) still decide whether their push and email actually fire —
          the in-app bell is always delivered.
        </p>

        {loading && <p className="text-sm text-gray-500">Loading…</p>}
        {!loading && events.length === 0 && (
          <p className="text-sm text-gray-500">No routable events yet.</p>
        )}

        {events.map((event) => (
          <div
            key={event.key}
            className="bg-white border border-[#e8e3d8] rounded-2xl p-5 shadow-[0_1px_2px_rgba(15,45,31,0.04)] space-y-4"
          >
            <div>
              <h2 className="font-semibold">{event.label}</h2>
              <p className="text-sm text-gray-500 mt-1">{event.description}</p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[520px]">
                <thead>
                  <tr className="text-left text-gray-500">
                    <th className="py-2 font-medium">Notify</th>
                    {channels.map((c) => (
                      <th key={c} className="py-2 px-3 text-center font-medium" title={CHANNEL_META[c]?.hint}>
                        {CHANNEL_META[c]?.label ?? c}
                      </th>
                    ))}
                    {event.supports_threshold && (
                      <th className="py-2 px-3 text-center font-medium" title={event.threshold_label}>
                        From #
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {recipients.map((r) => {
                    const cell = cellOf(event.key, r.key);
                    return (
                      <tr key={r.key} className="border-t border-[#f0ece2]">
                        <td className="py-2.5 pr-3">{r.label}</td>
                        {channels.map((c) => (
                          <td key={c} className="py-2.5 px-3 text-center">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded text-emerald-600"
                              disabled={readOnly}
                              checked={!!cell[c]}
                              aria-label={`${r.label} — ${CHANNEL_META[c]?.label ?? c}`}
                              onChange={(e) => setCell(event.key, r.key, { [c]: e.target.checked } as any)}
                            />
                          </td>
                        ))}
                        {event.supports_threshold && (
                          <td className="py-2.5 px-3 text-center">
                            <input
                              type="number"
                              min={0}
                              placeholder="—"
                              disabled={readOnly}
                              value={cell.threshold ?? ''}
                              aria-label={`${r.label} — ${event.threshold_label ?? 'threshold'}`}
                              onChange={(e) =>
                                setCell(event.key, r.key, {
                                  threshold: e.target.value === '' ? null : Number(e.target.value),
                                })
                              }
                              className="w-16 border rounded p-1 text-sm text-center disabled:bg-gray-50"
                            />
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {event.supports_threshold && (
              <p className="text-xs text-gray-400">
                “From #” = {event.threshold_label ?? 'only from this occurrence onwards'}.
                Blank means every time. Use it to loop HR in only on a repeat offender.
              </p>
            )}
          </div>
        ))}

        {!readOnly && events.length > 0 && (
          <div className="flex justify-end">
            <button
              onClick={save}
              disabled={saving}
              className="bg-emerald-700 text-white rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-60"
            >
              {saving ? 'Saving…' : 'Save routing'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
